import { apiService } from "./api.service";

class ProductService {
  // ============ PRIVATE HELPER METHODS ============

  /**
   * Get authentication token from cookie
   * @returns {string|null} Token if found, null otherwise
   */
  getAuthTokenFromCookie() {
    try {
      const cookies = document.cookie.split(";");
      for (let cookie of cookies) {
        const [name, value] = cookie.trim().split("=");
        if (name === "admin_access_token") {
          return value;
        }
      }
    } catch (error) {
      console.warn("Error reading auth token from cookie:", error);
    }
    return null;
  }

  /**
   * Check if user is authenticated
   * @returns {boolean} True if authenticated
   */
  isAuthenticated() {
    const token = this.getAuthTokenFromCookie();
    const userStr = localStorage.getItem("admin_user");
    return !!(token && userStr);
  }

  /**
   * Ensure authentication before making request
   * @throws {Error} If not authenticated
   */
  ensureAuthenticated() {
    if (!this.isAuthenticated()) {
      throw new Error("Not authenticated. Please login first.");
    }
  }

  /**
   * Get request config with proper authentication
   * @param {Object} customConfig - Custom configuration
   * @returns {Object} Request configuration
   */
  getRequestConfig(customConfig = {}) {
    const token = this.getAuthTokenFromCookie();
    const config = {
      withCredentials: true,
      ...customConfig,
    };

    // Add Authorization header if token exists
    if (token) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      };
    }

    return config;
  }

  /**
   * Handle API errors and retry logic
   * @param {Function} requestFn - The API request function
   * @param {number} retries - Number of retry attempts
   * @returns {Promise<any>} API response
   */
  async withRetry(requestFn, retries = 3) {
    for (let i = 0; i < retries; i++) {
      try {
        return await requestFn();
      } catch (error) {
        // If it's the last retry or not a retryable error, throw
        if (
          i === retries - 1 ||
          error?.response?.status === 401 ||
          error?.response?.status === 403
        ) {
          throw error;
        }

        // Wait before retrying (exponential backoff)
        await new Promise((resolve) =>
          setTimeout(resolve, Math.pow(2, i) * 1000),
        );
      }
    }
  }

  /**
   * Validate product data before sending to API
   * @param {Object} data - Product data
   * @param {boolean} isUpdate - Whether this is an update operation
   * @returns {Object} Validation result
   */
  validateProductData(data, isUpdate = false) {
    const errors = [];

    if (!isUpdate) {
      // Required for creation only
      if (!data.name || data.name.trim().length < 2) {
        errors.push("Product name must be at least 2 characters");
      }

      if (!data.slug || data.slug.trim().length < 2) {
        errors.push("Product slug must be at least 2 characters");
      }

      if (!data.sku || data.sku.trim().length < 2) {
        errors.push("Product SKU must be at least 2 characters");
      }

      if (!data.category_id) {
        errors.push("Category is required");
      }
    }

    // Always validate price if provided
    if (data.base_price !== undefined && data.base_price !== null) {
      if (
        isNaN(parseFloat(data.base_price)) ||
        parseFloat(data.base_price) <= 0
      ) {
        errors.push("Base price must be a positive number");
      }
    }

    // Validate sale price
    if (data.sale_price !== undefined && data.sale_price !== null) {
      const basePrice = parseFloat(data.base_price || 0);
      const salePrice = parseFloat(data.sale_price);
      if (isNaN(salePrice) || salePrice < 0) {
        errors.push("Sale price must be a non-negative number");
      }
      if (salePrice > basePrice) {
        errors.push("Sale price cannot be higher than base price");
      }
    }

    // Stock validation
    if (data.stock_quantity !== undefined && data.stock_quantity !== null) {
      if (
        isNaN(parseInt(data.stock_quantity)) ||
        parseInt(data.stock_quantity) < 0
      ) {
        errors.push("Stock quantity must be a non-negative integer");
      }
    }

    // Validate low_stock_threshold
    if (
      data.low_stock_threshold !== undefined &&
      data.low_stock_threshold !== null
    ) {
      if (
        isNaN(parseInt(data.low_stock_threshold)) ||
        parseInt(data.low_stock_threshold) < 0
      ) {
        errors.push("Low stock threshold must be a non-negative integer");
      }
    }

    // Validate min_order_quantity
    if (
      data.min_order_quantity !== undefined &&
      data.min_order_quantity !== null
    ) {
      if (
        isNaN(parseInt(data.min_order_quantity)) ||
        parseInt(data.min_order_quantity) < 1
      ) {
        errors.push("Minimum order quantity must be at least 1");
      }
    }

    // Validate max_order_quantity
    if (
      data.max_order_quantity !== undefined &&
      data.max_order_quantity !== null
    ) {
      const minQty = parseInt(data.min_order_quantity || 1);
      const maxQty = parseInt(data.max_order_quantity);
      if (isNaN(maxQty) || maxQty < 1) {
        errors.push("Maximum order quantity must be at least 1");
      }
      if (maxQty < minQty) {
        errors.push(
          "Maximum order quantity cannot be less than minimum order quantity",
        );
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Format product data for API
   * @param {Object} uiData - UI product data
   * @returns {Object} API-ready product data
   */
  formatForAPI(uiData) {
    // Format sizes as attributes
    const sizeAttributes = this.formatSizesAsAttributes(uiData.sizes);

    // Format colors as attributes
    const colorAttributes = this.formatColorsAsAttributes(uiData.colors);

    // Merge attributes
    const mergedAttributes = {
      ...uiData.attributes,
      ...sizeAttributes,
      ...colorAttributes,
    };

    // Create variants from size and color combinations if product is variable
    let variants = [];
    if (
      uiData.type === "variable" &&
      uiData.sizes?.length &&
      uiData.colors?.length
    ) {
      variants = this.createVariantsFromCombinations(uiData);
    } else {
      variants = Array.isArray(uiData.variants) ? uiData.variants : [];
    }

    // Handle images array - FIXED: changed apiData to uiData
    const images = Array.isArray(uiData.images)
      ? uiData.images.map((img, index) => {
          // For new images with file objects, we need to handle differently
          if (img.file instanceof File) {
            return img; // Keep the file object for FormData upload
          }

          // For existing images with IDs, just pass the ID
          return {
            id: img.id,
            is_primary: Boolean(img.is_primary),
            display_order: img.display_order || index,
            caption: img.caption || "",
            is_visible: Boolean(img.is_visible !== false),
          };
        })
      : [];

    // Handle specifications array
    const specifications = Array.isArray(uiData.specifications)
      ? uiData.specifications.map((spec) => ({
          key: spec.key || "",
          value: spec.value || "",
          group: spec.group || null,
          display_order: spec.display_order || 0,
          is_filterable: spec.is_filterable || false,
          is_visible: spec.is_visible ?? true,
          icon: spec.icon || null,
          unit: spec.unit || null,
          help_text: spec.help_text || null,
        }))
      : [];

    // Handle warranty period
    const warrantyPeriod = uiData.warranty_period
      ? {
          value: parseInt(uiData.warranty_period.value) || 0,
          unit: uiData.warranty_period.unit || "days",
          description: uiData.warranty_period.description || null,
        }
      : null;

    return {
      // Basic information
      name: uiData.name?.trim() || "",
      slug: uiData.slug?.trim() || "",
      description: uiData.description?.trim() || "",
      short_description: uiData.short_description?.trim() || "",
      excerpt: uiData.excerpt?.trim() || "",

      // Pricing
      base_price: uiData.base_price ? parseFloat(uiData.base_price) : undefined,
      sale_price:
        uiData.sale_price !== undefined
          ? uiData.sale_price
            ? parseFloat(uiData.sale_price)
            : null
          : undefined,
      cost_price:
        uiData.cost_price !== undefined
          ? uiData.cost_price
            ? parseFloat(uiData.cost_price)
            : null
          : undefined,
      compare_at_price:
        uiData.compare_at_price !== undefined
          ? uiData.compare_at_price
            ? parseFloat(uiData.compare_at_price)
            : null
          : undefined,
      tax_rate:
        uiData.tax_rate !== undefined
          ? uiData.tax_rate
            ? parseFloat(uiData.tax_rate)
            : null
          : undefined,

      // Inventory
      sku: uiData.sku?.trim() || "",
      stock_quantity:
        uiData.stock_quantity !== undefined
          ? parseInt(uiData.stock_quantity) || 0
          : undefined,
      low_stock_threshold:
        uiData.low_stock_threshold !== undefined
          ? parseInt(uiData.low_stock_threshold) || 10
          : undefined,
      is_track_inventory:
        uiData.is_track_inventory !== undefined
          ? Boolean(uiData.is_track_inventory)
          : undefined,
      allow_backorders:
        uiData.allow_backorders !== undefined
          ? Boolean(uiData.allow_backorders)
          : undefined,
      min_order_quantity:
        uiData.min_order_quantity !== undefined
          ? parseInt(uiData.min_order_quantity) || 1
          : undefined,
      max_order_quantity:
        uiData.max_order_quantity !== undefined
          ? uiData.max_order_quantity
            ? parseInt(uiData.max_order_quantity)
            : null
          : undefined,

      // Categorization
      category_id: uiData.category_id || undefined,
      brand:
        uiData.brand !== undefined ? uiData.brand?.trim() || null : undefined,
      manufacturer:
        uiData.manufacturer !== undefined
          ? uiData.manufacturer?.trim() || null
          : undefined,
      type: uiData.type !== undefined ? uiData.type || "simple" : undefined,

      // Physical properties
      weight:
        uiData.weight !== undefined
          ? uiData.weight
            ? parseFloat(uiData.weight)
            : null
          : undefined,
      weight_unit:
        uiData.weight_unit !== undefined
          ? uiData.weight_unit || null
          : undefined,
      dimensions: uiData.dimensions || undefined,
      shipping_class: uiData.shipping_class || null,
      requires_shipping:
        uiData.requires_shipping !== undefined
          ? Boolean(uiData.requires_shipping)
          : undefined,

      // Media and specifications
      images: images.length > 0 ? images : undefined,
      specifications: specifications.length > 0 ? specifications : undefined,
      variants: variants.length > 0 ? variants : undefined,
      attributes: mergedAttributes, // Store sizes and colors here
      features: Array.isArray(uiData.features) ? uiData.features : undefined,

      // Status flags
      is_active:
        uiData.is_active !== undefined ? Boolean(uiData.is_active) : undefined,
      is_featured:
        uiData.is_featured !== undefined
          ? Boolean(uiData.is_featured)
          : undefined,
      is_new: uiData.is_new !== undefined ? Boolean(uiData.is_new) : undefined,
      is_digital:
        uiData.is_digital !== undefined
          ? Boolean(uiData.is_digital)
          : undefined,
      is_taxable:
        uiData.is_taxable !== undefined
          ? Boolean(uiData.is_taxable)
          : undefined,
      is_visible:
        uiData.is_visible !== undefined
          ? Boolean(uiData.is_visible)
          : undefined,
      visibility: uiData.visibility || "visible",

      // SEO
      meta_title:
        uiData.meta_title !== undefined
          ? uiData.meta_title?.trim() || null
          : undefined,
      meta_description:
        uiData.meta_description !== undefined
          ? uiData.meta_description?.trim() || null
          : undefined,
      meta_keywords:
        uiData.meta_keywords !== undefined
          ? uiData.meta_keywords?.trim() || null
          : undefined,

      // Organization
      tags: Array.isArray(uiData.tags) ? uiData.tags : undefined,
      collections: Array.isArray(uiData.collections)
        ? uiData.collections
        : undefined,
      related_product_ids: Array.isArray(uiData.related_product_ids)
        ? uiData.related_product_ids
        : undefined,
      upsell_product_ids: Array.isArray(uiData.upsell_product_ids)
        ? uiData.upsell_product_ids
        : undefined,
      cross_sell_product_ids: Array.isArray(uiData.cross_sell_product_ids)
        ? uiData.cross_sell_product_ids
        : undefined,

      // Warranty
      warranty_period:
        warrantyPeriod !== undefined ? warrantyPeriod : undefined,

      // Timeline
      published_at: uiData.published_at || undefined,
      available_from: uiData.available_from || undefined,
      available_to: uiData.available_to || undefined,

      // Metadata
      metadata: {
        ...uiData.metadata,
        shipping_info: {
          weight: uiData.weight,
          weight_unit: uiData.weight_unit,
          dimensions: uiData.dimensions,
          shipping_class: uiData.shipping_class,
          requires_shipping: uiData.requires_shipping,
        },
      },
    };
  }
  /**
   * Format API response for UI display
   * @param {Object} apiData - API product data
   * @returns {Object} UI-ready product data
   */

  formatForUI(apiData) {
    // Calculate discount percentage
    const basePrice = parseFloat(apiData.base_price) || 0;
    const salePrice = apiData.sale_price
      ? parseFloat(apiData.sale_price)
      : null;
    const discountPercentage =
      salePrice && basePrice
        ? Math.round(((basePrice - salePrice) / basePrice) * 100)
        : null;

    let warrantyPeriod = null;
    if (apiData.warranty_period) {
      try {
        // If it's a string, try to parse it
        if (typeof apiData.warranty_period === "string") {
          warrantyPeriod = JSON.parse(apiData.warranty_period);
        }
        // If it's already an object
        else if (
          typeof apiData.warranty_period === "object" &&
          apiData.warranty_period !== null
        ) {
          warrantyPeriod = apiData.warranty_period;
        }

        // Ensure it has the expected structure
        if (warrantyPeriod && typeof warrantyPeriod === "object") {
          warrantyPeriod = {
            value: parseInt(warrantyPeriod.value) || 0,
            unit: warrantyPeriod.unit || "months",
            provider: warrantyPeriod.provider || null,
            description: warrantyPeriod.description || "",
          };
        }
      } catch (e) {
        console.warn("Failed to parse warranty period:", e);
        warrantyPeriod = null;
      }
    }

    // Format images array
    const images = Array.isArray(apiData.images)
      ? apiData.images.map((img, index) => {
          const fixImageUrl = (url) => {
            if (!url) return null;

            if (url.startsWith("/api/images/")) {
              return url.replace("/api/images/", "/api/products/images/");
            }

            if (url.startsWith("/images/")) {
              return `/api/products/images${url}`;
            }

            return url;
          };

          return {
            id: img.id,
            url: fixImageUrl(img.url),
            thumbnail_url: fixImageUrl(img.thumbnail_url),
            is_primary: Boolean(img.is_primary),
            is_visible: Boolean(img.is_visible !== false),
            display_order: img.display_order || index,
            caption: img.caption || "",
            file: null,
            mime_type: img.mime_type || "image/jpeg",
            size_bytes: img.size_bytes,
            width: img.width,
            height: img.height,
          };
        })
      : [];

    // Filter out images with null URLs
    const validImages = images.filter((img) => img.url);

    // Get main image (primary or first)
    const mainImage =
      validImages.find((img) => img.is_primary) || validImages[0] || null;

    // Extract sizes from attributes
    const sizes = this.extractSizesFromAttributes(apiData.attributes || {});

    // Extract colors from attributes
    const colors = this.extractColorsFromAttributes(apiData.attributes || {});

    // Format specifications
    const specifications = Array.isArray(apiData.specifications)
      ? apiData.specifications.map((spec) => ({
          id: spec.id,
          key: spec.key,
          value: spec.value,
          group: spec.group,
          icon: spec.icon,
          unit: spec.unit,
          help_text: spec.help_text,
          display_order: spec.display_order,
          is_filterable: spec.is_filterable,
          is_visible: spec.is_visible,
        }))
      : [];

    // Format variants
    const variants = Array.isArray(apiData.variants)
      ? apiData.variants.map((variant) => ({
          id: variant.id,
          sku: variant.sku,
          price: parseFloat(variant.price) || 0,
          sale_price: variant.sale_price
            ? parseFloat(variant.sale_price)
            : null,
          stock_quantity: parseInt(variant.stock_quantity) || 0,
          available_quantity: parseInt(variant.available_quantity) || 0,
          attributes: variant.attributes || {},
          is_active: variant.is_active,
          is_default: variant.is_default,
          weight: variant.weight ? parseFloat(variant.weight) : null,
          weight_unit: variant.weight_unit,
          dimensions: variant.dimensions,
        }))
      : [];

    return {
      // Basic information
      id: apiData.id,
      name: apiData.name,
      slug: apiData.slug,
      description: apiData.description || "",
      short_description: apiData.short_description || "",
      excerpt: apiData.excerpt || "",
      images: images,
      main_image: mainImage,

      // Pricing
      base_price: parseFloat(apiData.base_price) || 0,
      sale_price: apiData.sale_price ? parseFloat(apiData.sale_price) : null,
      cost_price: apiData.cost_price ? parseFloat(apiData.cost_price) : null,
      compare_at_price: apiData.compare_at_price
        ? parseFloat(apiData.compare_at_price)
        : null,
      tax_rate: apiData.tax_rate ? parseFloat(apiData.tax_rate) : null,
      discount_percentage: discountPercentage,
      formatted_price: `${basePrice.toFixed(2)} ج.م`,
      formatted_sale_price: salePrice ? `${salePrice.toFixed(2)} ج.م` : null,
      is_sale: !!salePrice,

      // Inventory
      sku: apiData.sku,
      stock_quantity: parseInt(apiData.stock_quantity) || 0,
      available_quantity: parseInt(apiData.available_quantity) || 0,
      reserved_quantity: parseInt(apiData.reserved_quantity) || 0,
      low_stock_threshold: parseInt(apiData.low_stock_threshold) || 10,
      min_order_quantity: parseInt(apiData.min_order_quantity) || 1,
      max_order_quantity: apiData.max_order_quantity
        ? parseInt(apiData.max_order_quantity)
        : null,
      is_track_inventory: Boolean(apiData.is_track_inventory),
      allow_backorders: Boolean(apiData.allow_backorders),
      inventory_status: apiData.inventory_status || "in_stock",

      // Categorization
      category_id: apiData.category_id,
      category_name: apiData.category_name || null,
      category_slug: apiData.category_slug || null,
      brand: apiData.brand || "",
      manufacturer: apiData.manufacturer || "",
      type: apiData.type || "simple",

      // Physical properties
      weight: apiData.weight ? parseFloat(apiData.weight) : null,
      weight_unit: apiData.weight_unit,
      dimensions: apiData.dimensions || null,
      shipping_class: apiData.shipping_class || null,
      requires_shipping: Boolean(apiData.requires_shipping),

      // Sizes & Colors (extracted from attributes)
      sizes: sizes,
      colors: colors,

      // Specifications & Details
      specifications: specifications,
      variants: variants,
      attributes: apiData.attributes || {},
      features: apiData.features || [],
      tags: apiData.tags || [],
      warranty_period: warrantyPeriod,

      // Status flags
      is_active: Boolean(apiData.is_active),
      is_featured: Boolean(apiData.is_featured),
      is_new: Boolean(apiData.is_new),
      is_bestseller: Boolean(apiData.is_bestseller),
      is_digital: Boolean(apiData.is_digital),
      is_virtual: Boolean(apiData.is_virtual),
      is_taxable: Boolean(apiData.is_taxable),
      is_visible: Boolean(apiData.is_visible),
      visibility: apiData.visibility || "visible",

      // SEO
      meta_title: apiData.meta_title || "",
      meta_description: apiData.meta_description || "",
      meta_keywords: apiData.meta_keywords || "",

      // Analytics
      view_count: parseInt(apiData.view_count) || 0,
      purchase_count: parseInt(apiData.purchase_count) || 0,
      wishlist_count: parseInt(apiData.wishlist_count) || 0,
      rating_avg: parseFloat(apiData.rating_avg) || 0,
      rating_count: parseInt(apiData.rating_count) || 0,
      review_count: parseInt(apiData.review_count) || 0,

      // Timeline
      published_at: apiData.published_at,
      available_from: apiData.available_from,
      available_to: apiData.available_to,

      // Audit fields
      created_at: apiData.created_at,
      updated_at: apiData.updated_at,
      created_by: apiData.created_by,
      updated_by: apiData.updated_by,
    };
  }

  /**
   * Format sizes as attributes for API
   * @param {Array} sizes - Sizes array from UI
   * @returns {Object} Attributes object for sizes
   */
  formatSizesAsAttributes(sizes) {
    if (!Array.isArray(sizes) || sizes.length === 0) return {};

    const sizeAttributes = {
      has_sizes: true,
      size_options: sizes.map((size) => ({
        value: size.name,
        label: size.name,
        sku: size.sku,
        stock: parseInt(size.stock_quantity) || 0,
        price_adjustment: parseFloat(size.price_adjustment) || 0,
      })),
    };

    return sizeAttributes;
  }

  /**
   * Format colors as attributes for API
   * @param {Array} colors - Colors array from UI
   * @returns {Object} Attributes object for colors
   */
  formatColorsAsAttributes(colors) {
    if (!Array.isArray(colors) || colors.length === 0) return {};

    const colorAttributes = {
      has_colors: true,
      color_options: colors.map((color) => ({
        value: color.name,
        label: color.name,
        hex: color.hex,
        sku: color.sku,
        stock: parseInt(color.stock_quantity) || 0,
        price_adjustment: parseFloat(color.price_adjustment) || 0,
        image: color.image || null,
      })),
    };

    return colorAttributes;
  }

  /**
   * Extract sizes from product attributes
   * @param {Object} attributes - Product attributes from API
   * @returns {Array} Sizes array for UI
   */
  extractSizesFromAttributes(attributes) {
    if (!attributes) return [];

    // Check if sizes exist directly in attributes
    if (attributes.sizes && Array.isArray(attributes.sizes)) {
      return attributes.sizes.map((size, index) => ({
        id: `size-${Date.now()}-${index}-${Math.random()}`,
        name: size.name || size.value || size.label || "",
        sku: size.sku || "",
        stock_quantity: parseInt(size.stock_quantity || size.stock) || 0,
        price_adjustment: parseFloat(size.price_adjustment) || 0,
      }));
    }

    // Fallback to size_options if they exist
    if (attributes.has_sizes && attributes.size_options) {
      const sizeOptions = attributes.size_options || [];
      return sizeOptions.map((size, index) => ({
        id: `size-${index}-${Date.now()}`,
        name: size.value || size.label || "",
        sku: size.sku || "",
        stock_quantity: parseInt(size.stock) || 0,
        price_adjustment: parseFloat(size.price_adjustment) || 0,
      }));
    }

    return [];
  }

  /**
   * Extract colors from product attributes
   * @param {Object} attributes - Product attributes from API
   * @returns {Array} Colors array for UI
   */
  extractColorsFromAttributes(attributes) {
    if (!attributes) return [];

    // Check if colors exist directly in attributes
    if (attributes.colors && Array.isArray(attributes.colors)) {
      return attributes.colors.map((color, index) => ({
        id: `color-${Date.now()}-${index}-${Math.random()}`,
        name: color.name || color.value || color.label || "",
        hex: color.hex || "#000000",
        sku: color.sku || "",
        stock_quantity: parseInt(color.stock_quantity || color.stock) || 0,
        price_adjustment: parseFloat(color.price_adjustment) || 0,
        image: color.image || null,
      }));
    }

    // Fallback to color_options if they exist
    if (attributes.has_colors && attributes.color_options) {
      const colorOptions = attributes.color_options || [];
      return colorOptions.map((color, index) => ({
        id: `color-${index}-${Date.now()}`,
        name: color.value || color.label || "",
        hex: color.hex || "#000000",
        sku: color.sku || "",
        stock_quantity: parseInt(color.stock) || 0,
        price_adjustment: parseFloat(color.price_adjustment) || 0,
        image: color.image || null,
      }));
    }

    return [];
  }

  /**
   * Create variants from size and color combinations
   * @param {Object} formData - Form data from UI
   * @returns {Array} Variants array for API
   */
  createVariantsFromCombinations(formData) {
    if (!formData.sizes?.length || !formData.colors?.length) return [];

    const variants = [];
    const basePrice = parseFloat(formData.base_price) || 0;

    formData.sizes.forEach((size) => {
      formData.colors.forEach((color) => {
        const sizeAdjustment = parseFloat(size.price_adjustment) || 0;
        const colorAdjustment = parseFloat(color.price_adjustment) || 0;
        const totalAdjustment = sizeAdjustment + colorAdjustment;

        variants.push({
          sku: `${formData.sku}-${size.name}-${color.name}`,
          price: basePrice + totalAdjustment,
          sale_price: formData.sale_price
            ? parseFloat(formData.sale_price) + totalAdjustment
            : null,
          stock_quantity: Math.min(
            parseInt(size.stock_quantity) || 0,
            parseInt(color.stock_quantity) || 0,
          ),
          attributes: {
            size: size.name,
            color: color.name,
          },
          is_active: true,
          is_default: variants.length === 0,
          requires_shipping: formData.requires_shipping,
          is_digital: formData.is_digital,
          is_taxable: formData.is_taxable,
        });
      });
    });

    return variants;
  }

  // ============ REVIEW MANAGEMENT ============

  /**
   * Get product reviews
   * @param {string} productId - Product ID
   * @param {Object} params - Query parameters (page, limit, rating, sort, etc.)
   * @returns {Promise<Object>} Reviews response
   */
  async getProductReviews(productId, params = {}, isAdmin = false) {
    try {
      const queryParams = new URLSearchParams();

      if (params.page) queryParams.append("page", params.page);
      if (params.limit) queryParams.append("limit", params.limit);
      if (params.sort_by) queryParams.append("sort_by", params.sort_by);
      if (params.sort_order)
        queryParams.append("sort_order", params.sort_order);

      // Important: Send is_approved as a boolean
      if (params.is_approved !== undefined) {
        queryParams.append("is_approved", params.is_approved);
      }

      if (params.rating && params.rating.length) {
        params.rating.forEach((r) => queryParams.append("rating[]", r));
      }

      // Use different URL for admin vs public
      const basePath = isAdmin
        ? "/products/admin/products"
        : "/products/products";
      const url = `${basePath}/${productId}/reviews?${queryParams.toString()}`;

      const config = this.getRequestConfig();

      const response = await this.withRetry(() => apiService.get(url, config));

      return response;
    } catch (error) {
      return {
        success: false,
        error: error.message,
        data: [],
      };
    }
  }

  /**
   * Get review statistics for a product
   * @param {string} productId - Product ID
   * @returns {Promise<Object>} Review statistics
   */
  async getReviewStats(productId) {
    try {
      const config = this.getRequestConfig();
      const response = await this.withRetry(() =>
        apiService.get(`/products/products/${productId}/reviews/stats`, config),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch review statistics",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  /**
   * Create a new product review (supports both guests and authenticated users)
   * @param {string} productId - Product ID
   * @param {Object} reviewData - Review data
   * @returns {Promise<Object>} Created review response
   */
  async createReview(productId, reviewData) {
    try {
      // Don't require authentication for reviews
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.post(
          `/products/products/${productId}/reviews`,
          reviewData,
          config,
        ),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to submit review",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  /**
   * Mark a review as helpful
   * @param {string} reviewId - Review ID
   * @returns {Promise<Object>} Response
   */
  async markReviewHelpful(reviewId) {
    try {
      const config = this.getRequestConfig();
      const response = await this.withRetry(() =>
        apiService.post(`/products/reviews/${reviewId}/helpful`, {}, config),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to mark review as helpful",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  /**
   * Mark a review as unhelpful
   * @param {string} reviewId - Review ID
   * @returns {Promise<Object>} Response
   */
  async markReviewUnhelpful(reviewId) {
    try {
      const config = this.getRequestConfig();
      const response = await this.withRetry(() =>
        apiService.post(`/products/reviews/${reviewId}/unhelpful`, {}, config),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to mark review as unhelpful",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  /**
   * Report a review
   * @param {string} reviewId - Review ID
   * @param {string} reason - Report reason
   * @param {string} email - Optional email for guest reporters
   * @returns {Promise<Object>} Response
   */
  async reportReview(reviewId, reason, email = null) {
    try {
      const config = this.getRequestConfig();
      const payload = { reason };
      if (email) payload.email = email;

      const response = await this.withRetry(() =>
        apiService.post(
          `/products/reviews/${reviewId}/report`,
          payload,
          config,
        ),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to report review",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  // ============ ADMIN REVIEW MANAGEMENT ============

  /**
   * Moderate a review (Admin only)
   * @param {string} reviewId - Review ID
   * @param {Object} moderation - Moderation actions (is_approved, is_featured, is_spam, notes)
   * @returns {Promise<Object>} Response
   */
  async moderateReview(reviewId, moderation) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      const response = await this.withRetry(() =>
        apiService.put(
          `/products/reviews/${reviewId}/moderate`,
          moderation,
          config,
        ),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to moderate review",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  /**
   * Delete a review (Admin only)
   * @param {string} reviewId - Review ID
   * @returns {Promise<Object>} Response
   */
  async deleteReview(reviewId) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      const response = await this.withRetry(() =>
        apiService.delete(`/products/reviews/${reviewId}`, config),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to delete review",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  /**
   * Bulk approve reviews (Admin only)
   * @param {Array} reviewIds - Array of review IDs
   * @returns {Promise<Object>} Response
   */
  async bulkApproveReviews(reviewIds) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      const response = await this.withRetry(() =>
        apiService.post(
          `/products/reviews/bulk/approve`,
          { reviewIds },
          config,
        ),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to bulk approve reviews",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  /**
   * Add a reply to a review (Admin only)
   * @param {string} reviewId - Review ID
   * @param {string} content - Reply content
   * @param {boolean} isPublic - Whether reply is public
   * @returns {Promise<Object>} Response
   */
  async addReviewReply(reviewId, content, isPublic = true) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      const response = await this.withRetry(() =>
        apiService.post(
          `/products/reviews/${reviewId}/reply`,
          { content, is_public: isPublic },
          config,
        ),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to add review reply",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  /**
   * Bulk delete reviews (Admin only)
   * @param {Array} reviewIds - Array of review IDs
   * @returns {Promise<Object>} Response
   */
  async bulkDeleteReviews(reviewIds) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      const response = await this.withRetry(() =>
        apiService.post(`/products/reviews/bulk/delete`, { reviewIds }, config),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to bulk delete reviews",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  /**
   * Bulk feature reviews (Admin only)
   * @param {Array} reviewIds - Array of review IDs
   * @returns {Promise<Object>} Response
   */
  async bulkFeatureReviews(reviewIds) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      const response = await this.withRetry(() =>
        apiService.post(
          `/products/reviews/bulk/feature`,
          { reviewIds },
          config,
        ),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to bulk feature reviews",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  /**
   * Get pending reviews (Admin only)
   * @param {Object} params - Pagination params (page, limit)
   * @returns {Promise<Object>} Pending reviews response
   */
  async getPendingReviews(params = {}) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig({ params });
      const response = await this.withRetry(() =>
        apiService.get(`/products/reviews/pending`, config),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch pending reviews",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  /**
   * Get customer reviews by email (Admin only)
   * @param {string} email - Customer email
   * @param {Object} params - Pagination params (page, limit)
   * @returns {Promise<Object>} Customer reviews response
   */
  async getCustomerReviews(email, params = {}) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig({ params });
      const response = await this.withRetry(() =>
        apiService.get(
          `/products/reviews/customer/${encodeURIComponent(email)}`,
          config,
        ),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch customer reviews",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  // ============ PUBLIC ENDPOINTS ============

  async getProducts(params = {}) {
    try {
      const config = this.getRequestConfig({ params });
      const response = await this.withRetry(() =>
        apiService.get("/products/products", config),
      );

      // Format the response data for UI
      if (response.data?.data) {
        response.data.data = response.data.data.map((product) =>
          this.formatForUI(product),
        );
      } else if (Array.isArray(response.data)) {
        response.data = response.data.map((product) =>
          this.formatForUI(product),
        );
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch products",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  async getFeaturedProducts(limit = 10) {
    try {
      const config = this.getRequestConfig({
        params: {
          is_featured: true,
          limit,
          sort_by: "created_at",
          sort_order: "DESC",
        },
      });
      const response = await this.withRetry(() =>
        apiService.get("/products/products", config),
      );

      if (response.data?.data) {
        response.data.data = response.data.data.map(
          this.formatForUI.bind(this),
        );
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch featured products",
        message: error.message,
      };
    }
  }

  async getNewProducts(limit = 10) {
    try {
      const config = this.getRequestConfig({
        params: {
          is_new: true,
          limit,
          sort_by: "created_at",
          sort_order: "DESC",
        },
      });
      const response = await this.withRetry(() =>
        apiService.get("/products/products", config),
      );

      if (response.data?.data) {
        response.data.data = response.data.data.map(
          this.formatForUI.bind(this),
        );
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch new products",
        message: error.message,
      };
    }
  }

  async getBestsellerProducts(limit = 10) {
    try {
      const config = this.getRequestConfig({
        params: {
          is_bestseller: true,
          limit,
          sort_by: "purchase_count",
          sort_order: "DESC",
        },
      });
      const response = await this.withRetry(() =>
        apiService.get("/products/products", config),
      );

      if (response.data?.data) {
        response.data.data = response.data.data.map(
          this.formatForUI.bind(this),
        );
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch bestseller products",
        message: error.message,
      };
    }
  }

  async getSaleProducts(limit = 10) {
    try {
      const config = this.getRequestConfig({
        params: {
          is_sale: true,
          limit,
          sort_by: "created_at",
          sort_order: "DESC",
        },
      });
      const response = await this.withRetry(() =>
        apiService.get("/products/products", config),
      );

      if (response.data?.data) {
        response.data.data = response.data.data.map(
          this.formatForUI.bind(this),
        );
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch sale products",
        message: error.message,
      };
    }
  }

  async searchProducts(query, params = {}) {
    try {
      const config = this.getRequestConfig({
        params: {
          q: query,
          ...params,
        },
      });
      const response = await this.withRetry(() =>
        apiService.get("/products/products/search", config),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to search products",
        message: error.message,
      };
    }
  }

  async getProductsByCategory(categoryId, params = {}) {
    try {
      const config = this.getRequestConfig({ params });
      const response = await this.withRetry(() =>
        apiService.get(`/products/products/category/${categoryId}`, config),
      );

      if (response.data?.data) {
        response.data.data = response.data.data.map(
          this.formatForUI.bind(this),
        );
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch products by category",
        message: error.message,
      };
    }
  }

  async getProductsByTag(tag, params = {}) {
    try {
      const config = this.getRequestConfig({
        params: { tags: [tag], ...params },
      });
      const response = await this.withRetry(() =>
        apiService.get("/products/products", config),
      );

      if (response.data?.data) {
        response.data.data = response.data.data.map(
          this.formatForUI.bind(this),
        );
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch products by tag",
        message: error.message,
      };
    }
  }

  async getProductsByBrand(brand, params = {}) {
    try {
      const config = this.getRequestConfig({
        params: { brands: [brand], ...params },
      });
      const response = await this.withRetry(() =>
        apiService.get("/products/products", config),
      );

      if (response.data?.data) {
        response.data.data = response.data.data.map(
          this.formatForUI.bind(this),
        );
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch products by brand",
        message: error.message,
      };
    }
  }

  async getProduct(idOrSlug) {
    try {
      const config = this.getRequestConfig();
      const response = await this.withRetry(() =>
        apiService.get(`/products/products/${idOrSlug}`, config),
      );

      if (response.data) {
        response.data = this.formatForUI(response.data);
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch product",
        message: error.message,
      };
    }
  }

  async getProductBySlug(slug) {
    return this.getProduct(slug);
  }

  async getRelatedProducts(id, limit = 4) {
    try {
      const config = this.getRequestConfig({ params: { limit } });
      const response = await this.withRetry(() =>
        apiService.get(`/products/products/${id}/related`, config),
      );

      if (response.data?.data) {
        response.data.data = response.data.data.map(
          this.formatForUI.bind(this),
        );
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch related products",
        message: error.message,
      };
    }
  }

  // Product tracking
  async incrementViewCount(id) {
    try {
      const config = this.getRequestConfig();
      return await this.withRetry(() =>
        apiService.post(`/products/products/${id}/view`, {}, config),
      );
    } catch (error) {
      return {
        success: false,
        error: "Failed to increment view count",
        message: error.message,
      };
    }
  }

  // ============ ADMIN ENDPOINTS ============

  async createProduct(data) {
    try {
      this.ensureAuthenticated();

      // Validate data
      const validation = this.validateProductData(data, false);
      if (!validation.isValid) {
        return {
          success: false,
          error: "Validation failed",
          message: validation.errors.join(", "),
          errors: validation.errors,
        };
      }

      // Format for API
      const apiData = this.formatForAPI(data);
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.post("/products/products", apiData, config),
      );

      if (response.data) {
        response.data = this.formatForUI(response.data);
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to create product",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  async updateProduct(id, data) {
    try {
      this.ensureAuthenticated();

      // Validate data
      const validation = this.validateProductData(data, true);
      if (!validation.isValid) {
        return {
          success: false,
          error: "Validation failed",
          message: validation.errors.join(", "),
          errors: validation.errors,
        };
      }

      // Format for API
      const apiData = this.formatForAPI(data);
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.put(`/products/products/${id}`, apiData, config),
      );

      if (response.data) {
        response.data = this.formatForUI(response.data);
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to update product",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  async deleteProduct(id) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      return await this.withRetry(() =>
        apiService.delete(`/products/products/${id}`, config),
      );
    } catch (error) {
      return {
        success: false,
        error: "Failed to delete product",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  // ============ INVENTORY MANAGEMENT ============

  async updateInventory(data) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      return await this.withRetry(() =>
        apiService.put("/products/products/inventory/update", data, config),
      );
    } catch (error) {
      return {
        success: false,
        error: "Failed to update inventory",
        message: error.message,
      };
    }
  }

  async bulkUpdateInventory(data) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      return await this.withRetry(() =>
        apiService.post(
          "/products/products/inventory/bulk-update",
          data,
          config,
        ),
      );
    } catch (error) {
      return {
        success: false,
        error: "Failed to bulk update inventory",
        message: error.message,
      };
    }
  }

  async getLowStockProducts(params = {}) {
    try {
      const config = this.getRequestConfig({
        params: { inventory_status: "low_stock", ...params },
      });
      return await this.withRetry(() =>
        apiService.get("/products/products", config),
      );
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch low stock products",
        message: error.message,
      };
    }
  }

  async getOutOfStockProducts(params = {}) {
    try {
      const config = this.getRequestConfig({
        params: { inventory_status: "out_of_stock", ...params },
      });
      return await this.withRetry(() =>
        apiService.get("/products/products", config),
      );
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch out of stock products",
        message: error.message,
      };
    }
  }

  // ============ STATUS MANAGEMENT ============

  async toggleFeaturedStatus(id) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      return await this.withRetry(() =>
        apiService.put(`/products/products/${id}/featured`, {}, config),
      );
    } catch (error) {
      return {
        success: false,
        error: "Failed to toggle featured status",
        message: error.message,
      };
    }
  }

  async toggleActiveStatus(id) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      return await this.withRetry(() =>
        apiService.put(`/products/products/${id}/active`, {}, config),
      );
    } catch (error) {
      return {
        success: false,
        error: "Failed to toggle active status",
        message: error.message,
      };
    }
  }

  async toggleNewStatus(id) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      return await this.withRetry(() =>
        apiService.put(`/products/products/${id}/new`, {}, config),
      );
    } catch (error) {
      return {
        success: false,
        error: "Failed to toggle new status",
        message: error.message,
      };
    }
  }

  // ============ PRICE MANAGEMENT ============

  async updateProductPrice(id, priceData) {
    try {
      this.ensureAuthenticated();

      const validation = this.validateProductData(
        {
          base_price: priceData.base_price,
          sale_price: priceData.sale_price,
        },
        true,
      );

      if (!validation.isValid) {
        return {
          success: false,
          error: "Validation failed",
          message: validation.errors.join(", "),
        };
      }

      const config = this.getRequestConfig();
      return await this.withRetry(() =>
        apiService.put(`/products/products/${id}/price`, priceData, config),
      );
    } catch (error) {
      return {
        success: false,
        error: "Failed to update product price",
        message: error.message,
      };
    }
  }

  // ============ ANALYTICS ============

  async getProductAnalytics(id, period = "month") {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig({ params: { period } });
      return await this.withRetry(() =>
        apiService.get(`/products/products/${id}/analytics`, config),
      );
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch product analytics",
        message: error.message,
      };
    }
  }

  async getProductsAnalytics(params = {}) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig({ params });
      return await this.withRetry(() =>
        apiService.get("/products/products/analytics/overview", config),
      );
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch products analytics",
        message: error.message,
      };
    }
  }

  // ============ BULK OPERATIONS ============

  async bulkUpdateStatus(productIds, isActive) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      return await this.withRetry(() =>
        apiService.post(
          "/products/products/bulk/status",
          {
            product_ids: productIds,
            is_active: isActive,
          },
          config,
        ),
      );
    } catch (error) {
      return {
        success: false,
        error: "Failed to bulk update product status",
        message: error.message,
      };
    }
  }

  async bulkDeleteProducts(productIds) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      return await this.withRetry(() =>
        apiService.post(
          "/products/products/bulk/delete",
          {
            product_ids: productIds,
          },
          config,
        ),
      );
    } catch (error) {
      return {
        success: false,
        error: "Failed to bulk delete products",
        message: error.message,
      };
    }
  }

  // ============ EXPORT ============

  async exportProducts(format = "csv") {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig({ responseType: "blob" });
      return await this.withRetry(() =>
        apiService.get(`/products/products/export?format=${format}`, config),
      );
    } catch (error) {
      return {
        success: false,
        error: "Failed to export products",
        message: error.message,
      };
    }
  }

  // ============ IMAGE MANAGEMENT ============

  /**
   * Upload multiple images for a product
   * @param {string} productId - Product ID
   * @param {Array<File>} files - Array of image files
   * @param {Object} options - Additional options (caption, is_primary, display_order, is_visible, variant_id, etc.)
   * @returns {Promise<Object>} Response with imageIds
   */
  async addProductImages(productId, files, options = {}) {
    try {
      this.ensureAuthenticated();

      const formData = new FormData();
      files.forEach((file) => {
        formData.append("images", file);
      });

      // Append options as form fields
      if (options.caption) formData.append("caption", options.caption);
      if (options.is_primary !== undefined)
        formData.append("is_primary", String(options.is_primary));
      if (options.display_order !== undefined)
        formData.append("display_order", String(options.display_order));
      if (options.is_visible !== undefined)
        formData.append("is_visible", String(options.is_visible));
      if (options.generate_thumbnail !== undefined)
        formData.append(
          "generate_thumbnail",
          String(options.generate_thumbnail),
        );

      const config = this.getRequestConfig({
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const response = await this.withRetry(() =>
        apiService.post(
          `/products/products/${productId}/images`,
          formData,
          config,
        ),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to upload product images",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  /**
   * Update an existing product image (optionally with a new file)
   * @param {string} imageId - Image ID
   * @param {Object} data - Update data (caption, is_primary, display_order, is_visible, etc.)
   * @param {File} [newFile] - Optional new image file
   * @returns {Promise<Object>} Response
   */
  async updateProductImage(imageId, data, newFile = null) {
    try {
      this.ensureAuthenticated();

      const formData = new FormData();

      if (newFile) {
        formData.append("image", newFile);
      }

      // Append other fields
      if (data.caption !== undefined) formData.append("caption", data.caption);
      if (data.is_primary !== undefined)
        formData.append("is_primary", String(data.is_primary));
      if (data.display_order !== undefined)
        formData.append("display_order", String(data.display_order));
      if (data.is_visible !== undefined)
        formData.append("is_visible", String(data.is_visible));
      if (data.variant_id !== undefined)
        formData.append("variant_id", data.variant_id);

      const config = this.getRequestConfig({
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const response = await this.withRetry(() =>
        apiService.put(`/products/images/${imageId}`, formData, config),
      );

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to update product image",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  /**
   * Delete a product image
   * @param {string} imageId - Image ID
   * @returns {Promise<Object>} Response
   */
  async deleteProductImage(imageId) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      const response = await this.withRetry(() =>
        apiService.delete(`/products/images/${imageId}`, config),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to delete product image",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  async getProductImage(imageId) {
    try {
      const config = this.getRequestConfig();
      const response = await this.withRetry(() =>
        apiService.get(`/products/images/${imageId}`, config),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch product image",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  async getImageData(imageId, format = "json") {
    try {
      const config = this.getRequestConfig({ params: { format } });
      const response = await this.withRetry(() =>
        apiService.get(`/products/images/${imageId}/data`, config),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch image data",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  async getImageThumbnail(imageId) {
    try {
      const config = this.getRequestConfig({ responseType: "blob" });
      const response = await this.withRetry(() =>
        apiService.get(`/products/images/${imageId}/thumbnail`, config),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch image thumbnail",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  /**
   * Set an image as primary for a product
   * @param {string} productId - Product ID
   * @param {string} imageId - Image ID
   * @returns {Promise<Object>} Response
   */
  async setPrimaryImage(productId, imageId) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      const response = await this.withRetry(() =>
        apiService.put(
          `/products/products/${productId}/images/${imageId}/primary`,
          {},
          config,
        ),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to set primary image",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  /**
   * Reorder product images
   * @param {string} productId - Product ID
   * @param {Array<{imageId: string, display_order: number}>} imageOrder - Array of image order objects
   * @returns {Promise<Object>} Response
   */
  async reorderProductImages(productId, imageOrder) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      const response = await this.withRetry(() =>
        apiService.put(
          `/products/products/${productId}/images/reorder`,
          { images: imageOrder },
          config,
        ),
      );
      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to reorder product images",
        message: error.message,
        status: error.response?.status,
      };
    }
  }
}

export const productService = new ProductService();
